import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RTL_LANGUAGES } from "i18n";
import BrandItem from "./BrandItem";

const BRANDS = [
  { name: "Digikala",    icon: "◆" },
  { name: "Snapp",       icon: "✦" },
  { name: "Zarinpal",    icon: "●" },
  { name: "Tapsi",       icon: "◈" },
  { name: "Alibaba",     icon: "▲" },
  { name: "Bank Mellat", icon: "★" },
  { name: "Filimo",      icon: "◐" },
];

const SPEED = 55; // px / s

export default function Marquee() {
  const { i18n } = useTranslation();
  const baseLang = (i18n.language || "fa").split("-")[0];
  const isRTL = RTL_LANGUAGES.includes(baseLang);

  const containerRef = useRef(null);
  const measureRef = useRef(null);

  const [copies, setCopies] = useState(3);
  const [duration, setDuration] = useState(30);

  /* -------- اندازه‌گیری: یک بار + بعد از لود فونت -------- */
  useLayoutEffect(() => {
    const measure = () => {
      const measureEl = measureRef.current;
      const containerEl = containerRef.current;
      if (!measureEl || !containerEl) return;

      // عرض یک کپی کامل
      const oneW = measureEl.getBoundingClientRect().width;
      const containerW = containerEl.getBoundingClientRect().width;
      if (!oneW || !containerW) return;

      // N طوری که (N-1)*oneW >= containerW → هیچ gap نداشته باشیم
      const needed = Math.max(2, Math.ceil(containerW / oneW) + 1);
      setCopies(needed);

      // حرکت یک کپی = oneW پیکسل → زمان = oneW / SPEED
      setDuration(Math.max(8, oneW / SPEED));
    };

    measure();

    // دوباره بعد از لود فونت‌ها (چون عرض متن عوض می‌شه)
    if (document.fonts?.ready) {
      document.fonts.ready.then(measure);
    }

    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    if (measureRef.current) ro.observe(measureRef.current);

    return () => ro.disconnect();
  }, []);

  /* -------- جهت حرکت: RTL → محتوا به راست، LTR → به چپ -------- */
  const endX = isRTL
    ? `${100 / copies}%`    // مثبت → به راست
    : `${-100 / copies}%`;  // منفی → به چپ

  const trackStyle = {
    "--marquee-end": endX,
    animationDuration: `${duration}s`,
  };

  return (
    <section
      ref={containerRef}
      aria-label="Trusted by leading brands"
      className="marquee relative overflow-hidden border-y border-line bg-surface py-6 sm:py-8"
    >
      {/* fade masks */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 start-0 z-10 w-16 bg-gradient-to-r from-surface to-transparent sm:w-28 rtl:bg-gradient-to-l"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 end-0 z-10 w-16 bg-gradient-to-l from-surface to-transparent sm:w-28 rtl:bg-gradient-to-r"
      />

      {/* کپی مخفی برای اندازه‌گیری — یک بار رندر می‌شه */}
      <div
        ref={measureRef}
        aria-hidden
        className="invisible pointer-events-none absolute top-0 flex w-max"
        style={{ insetInlineStart: 0 }}
      >
        {BRANDS.map((b, i) => (
          <BrandItem key={`m-${b.name}-${i}`} {...b} />
        ))}
      </div>

      {/* نوار واقعی — N کپی */}
      <div className="marquee-track" style={trackStyle}>
        {Array.from({ length: copies }).map((_, c) =>
          BRANDS.map((b, i) => (
            <BrandItem key={`${c}-${b.name}-${i}`} {...b} />
          ))
        )}
      </div>
    </section>
  );
}